import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice"; // adjust import path as needed
import "./qu-add-question-modal.css";

export default function AddQuestionModal({ isOpen, onClose, questionId }) {
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [optionsCount, setOptionsCount] = useState(4);
  const [options, setOptions] = useState(Array(4).fill(""));
  const [correctAnswer, setCorrectAnswer] = useState("1");
  const [hintFile, setHintFile] = useState(null);
  const [description, setDescription] = useState("");
  const [explain, setExplain] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [existingHint, setExistingHint] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);
  const [optionFiles, setOptionFiles] = useState(Array(4).fill(null));
  const optionFileRefs = useRef([]);

  console.log(themeData);

  function fixEncoding(value) {
    return value;
  }

  // Fallback CP1252 decoder for browsers/environments without TextDecoder('windows-1252')
  function cp1252DecodeFallback(bytes) {
    const cp1252Map = {
      0x80: "\u20AC",
      0x81: "\u0081",
      0x82: "\u201A",
      0x83: "\u0192",
      0x84: "\u201E",
      0x85: "\u2026",
      0x86: "\u2020",
      0x87: "\u2021",
      0x88: "\u02C6",
      0x89: "\u2030",
      0x8a: "\u0160",
      0x8b: "\u2039",
      0x8c: "\u0152",
      0x8d: "\u008D",
      0x8e: "\u017D",
      0x8f: "\u008F",
      0x90: "\u0090",
      0x91: "\u2018",
      0x92: "\u2019",
      0x93: "\u201C",
      0x94: "\u201D",
      0x95: "\u2022",
      0x96: "\u2013",
      0x97: "\u2014",
      0x98: "\u02DC",
      0x99: "\u2122",
      0x9a: "\u0161",
      0x9b: "\u203A",
      0x9c: "\u0153",
      0x9d: "\u009D",
      0x9e: "\u017E",
      0x9f: "\u0178",

      // critical mojibake fixes
      0xfd: "\u2019", // ’ (right single quote)
      0xdd: "\u201D", // ” (right double quote)
    };

    let out = "";
    for (let i = 0; i < bytes.length; i++) {
      const b = bytes[i];

      // NEW: direct fix for problematic bytes
      if (b === 0xfd || b === 0xdd) {
        out += cp1252Map[b];
        continue;
      }

      if (b <= 0x7f) out += String.fromCharCode(b);
      else if (b >= 0x80 && b <= 0x9f)
        out += cp1252Map[b] || String.fromCharCode(b);
      else if (b >= 0xa0) out += String.fromCharCode(b);
    }

    return out;
  }

  // Load existing question when editing
  useEffect(() => {
    if (!isOpen) return;
    if (questionId) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/getQuestionById`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ id: questionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.question) {
            const q = data.question;
            setQuestion(q.question_name || "");
            const count = parseInt(q.showOption, 10) || 4;
            setOptionsCount(count);
            setOptions([
              q.option_one || "",
              q.option_two || "",
              q.option_three || "",
              q.option_four || "",
            ]);
            setCorrectAnswer(String(q.answer_id));
            // NEW: populate description & explain from API if present
            setDescription(q.description || "");
            setExplain(q.explaination || "");

            if (q.question_hint) {
              setExistingHint(q.question_hint);
              setPreviewUrl(
                `${process.env.REACT_APP_S3_PATH}${q.question_hint}`
              );
            }
          } else {
            Swal.fire("Error", "Failed to fetch question", "error");
            onClose();
          }
        })
        .catch(() => {
          Swal.fire("Error", "Failed to fetch question", "error");
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      // Reset for creation
      setQuestion("");
      setOptionsCount(4);
      setOptions(Array(4).fill(""));
      setCorrectAnswer("1");
      setHintFile(null);
      setExistingHint(null);
      setPreviewUrl(null);
      setRemoveImage(false);
      setDescription("");
      setExplain("");
      setOptionFiles(Array(4).fill(null));
    }
  }, [isOpen, questionId]);

  // update preview when hintFile changes
  useEffect(() => {
    if (hintFile) {
      const objectUrl = URL.createObjectURL(hintFile);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [hintFile]);

  const handleOptionsCountChange = (count) => {
    const isAllowed =
      !themeData.lifelines ||
      (count === 2 && !themeData.double && !themeData.fifty_fifty) ||
      count !== 2;

    if (isAllowed) {
      setOptionsCount(count);
      setOptions((prev) => {
        const newOpts = Array(count).fill("");
        for (let i = 0; i < Math.min(prev.length, count); i++) {
          newOpts[i] = prev[i];
        }
        return newOpts;
      });
      if (parseInt(correctAnswer) > count) {
        setCorrectAnswer("1");
      }
      setOptionFiles((prev) => {
        const newFiles = Array(count).fill(null);
        for (let i = 0; i < Math.min(prev.length, count); i++)
          newFiles[i] = prev[i];
        return newFiles;
      });
    } else {
      Swal.fire(
        "Update Required",
        "To use only 2 options you must disable Double Dip and 50:50 lifelines",
        "info"
      );
    }
  };

  const handleOptionChange = (idx, value) => {
    let newVal = value;
    if (value.length > 150) {
      Swal.fire(
        "Limit Exceeded",
        "Options can be at most 150 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = value.slice(0, 150);
    }
    setOptions((prev) => {
      const newOpts = [...prev];
      newOpts[idx] = newVal;
      return newOpts;
    });
  };

  // file validation helper (same rules as hint)
  const isValidOptionFile = (file) => {
    if (!file) return { ok: false, msg: "No file provided" };
    const validExt = ["jpeg", "jpg", "png"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!validExt.includes(ext)) {
      return { ok: false, msg: `Only ${validExt.join(", ")} allowed` };
    }
    if (file.size > 1 * 1024 * 1024) {
      return { ok: false, msg: "Maximum size is 1MB" };
    }
    return { ok: true };
  };

  const handleOptionFileChange = (index, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const v = isValidOptionFile(file);
    if (!v.ok) {
      Swal.fire("Invalid file", v.msg, "error");
      // clear the file input so user can retry
      e.target.value = "";
      return;
    }
    setOptionFiles((prev) => {
      const copy = [...prev];
      copy[index] = file;
      return copy;
    });
  };

  const removeOptionFile = (index) => {
    setOptionFiles((prev) => {
      const copy = [...prev];
      copy[index] = null;
      return copy;
    });
    // also clear value of the hidden file input if present
    if (optionFileRefs.current[index]) optionFileRefs.current[index].value = "";
  };

  const handleRemoveImage = () => {
    setRemoveImage(true);
    setPreviewUrl(null);
    setExistingHint(null);
  };

  const handleQuestionChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Question can be at most 300 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setQuestion(newVal);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validExt = [
      "jpeg",
      "jpg",
      "png",
      "mp4",
      "mp3",
      "wav",
      "m4a",
      "webm",
      "ogg",
    ];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!validExt.includes(ext)) {
      Swal.fire(
        "Invalid file type",
        `Only ${validExt.join(", ")} allowed`,
        "error"
      );
      e.target.value = "";
      setHintFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum size is 5MB", "error");
      e.target.value = "";
      setHintFile(null);
      return;
    }
    setHintFile(file);
  };

  const handleDescriptionChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Description can be at most 300 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setDescription(newVal);
  };

  const handleExplainChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Explanation can be at most 300 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setExplain(newVal);
  };

  useEffect(() => {
    if (hintFile) {
      const objectUrl = URL.createObjectURL(hintFile);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [hintFile]);

  const handleSubmit = async () => {
    if (!question.trim()) {
      Swal.fire("Error", "Question is required", "error");
      return;
    }
    for (let i = 0; i < optionsCount; i++) {
      const text = options[i]?.trim();
      const hasFile = optionFiles && optionFiles[i];
      if (!text && !hasFile) {
        Swal.fire("Error", `Option ${i + 1} requires text or a file`, "error");
        return;
      }
    }

    const formData = new FormData();
    if (questionId) formData.append("id", questionId);
    formData.append("question", question);
    const normalizedOptions = options
      .slice(0, optionsCount)
      .map((t, i) => (t ? t.trim() : ""));
    formData.append("options", JSON.stringify(normalizedOptions));
    formData.append("correctAnswer", correctAnswer);
    formData.append("currentTheme", currentTheme);
    formData.append("hintForceRemove", removeImage);
    formData.append("description", description);
    formData.append("explain", explain);
    if (hintFile) formData.append("hint", hintFile);

    for (let i = 0; i < optionsCount; i++) {
      if (optionFiles[i]) {
        formData.append(`option_file_${i + 1}`, optionFiles[i]);
      }
    }

    const endpoint = "/addNewQuestion";

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
          body: formData,
        }
      );
      if (!res.ok) throw new Error("Request failed");
      Swal.fire(
        "Success",
        questionId ? "Question updated" : "Question added",
        "success"
      );
      window.location.reload();
      onClose();
    } catch (err) {
      Swal.fire("Error", "Failed to save question", "error");
    }
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="qu-modal-backdrop" onClick={onClose} />
      <div className="qu-modal">
        <h2>
          {loading
            ? "Loading..."
            : questionId
            ? "Edit Question"
            : "Add New Question"}
        </h2>

        <div className="qu-form-group">
          <label>Question</label>
          <input
            type="text"
            value={fixEncoding(question)}
            onChange={(e) => handleQuestionChange(e.target.value)}
            placeholder="Enter question"
            disabled={loading}
          />
          <div className="code">* Max length 300 characters.</div>
        </div>
        <div className="qu-form-group">
          <label>Description (optional)</label>
          <input
            type="text"
            value={fixEncoding(description)}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder="Short description or context for the question (max 300 chars)"
            disabled={loading}
            rows={3}
          />
          <div className="code">* Max length 300 characters.</div>
        </div>

        <div className="qu-form-group">
          <label>Explanation (optional)</label>
          <input
            type="text"
            value={fixEncoding(explain)}
            onChange={(e) => handleExplainChange(e.target.value)}
            placeholder="Explanation to show after answering (max 300 chars)"
            disabled={loading}
            rows={4}
          />
          <div className="code">* Max length 300 characters.</div>
        </div>

        <div className="qu-options-count">
          <span></span>
          <div className="options-count-holder">
            {[4, 2].map((c) => (
              <button
                key={c}
                disabled={loading}
                className={`qu-count-btn ${
                  optionsCount === c ? "qu-active" : ""
                }`}
                onClick={() => handleOptionsCountChange(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="qu-form-group-v1">
          {options.slice(0, optionsCount).map((opt, i) => {
            const letter = String.fromCharCode(65 + i); // A, B, C, D
            const val = opt || "";

            // detect if option text is a media filename
            const isMedia = (name) =>
              !!name.match(/\.(jpeg|jpg|png|mp4|webm|ogg|mp3|wav|m4a)$/i);

            // dev mapping for your local test file
            const src =
              val === "2887d549-a046-4381-bcb1-7d9038c9d217.png"
                ? "/mnt/data/2887d549-a046-4381-bcb1-7d9038c9d217.png"
                : `${process.env.REACT_APP_S3_PATH}${val}`;

            return (
              <div className="input-option-holder" key={i}>
                <div className="input-label">{`Option ${letter}`}</div>

                {/* 1) New uploaded file → show filename + remove */}
                {optionFiles[i] ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span className="qu-file-name">{optionFiles[i].name}</span>
                    <button
                      type="button"
                      className="remove-image-button"
                      onClick={() => removeOptionFile(i)}
                      disabled={loading}
                    >
                      x
                    </button>
                  </div>
                ) : /* 2) Existing stored media → show media ONLY (no filename) */
                isMedia(val) ? (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    {val.match(/\.(mp4|webm|ogg)$/i) ? (
                      <video
                        src={src}
                        controls
                        style={{ maxWidth: 220, maxHeight: 140 }}
                      />
                    ) : val.match(/\.(mp3|wav|m4a)$/i) ? (
                      <audio src={src} controls />
                    ) : (
                      <img
                        src={src}
                        alt={`option-${i + 1}`}
                        className="options-image"
                        style={{ maxWidth: 220, maxHeight: 140 }}
                      />
                    )}

                    <button
                      type="button"
                      className="remove-image-button"
                      onClick={() => {
                        // remove media → show text input
                        setOptions((prev) => {
                          const copy = [...prev];
                          copy[i] = "";
                          return copy;
                        });
                      }}
                      disabled={loading}
                    >
                      x
                    </button>
                  </div>
                ) : (
                  /* 3) Plain text mode → show text input */
                  <input
                    type="text"
                    placeholder={`Option ${letter}`}
                    value={fixEncoding(opt)}
                    disabled={loading}
                    onChange={(e) => handleOptionChange(i, e.target.value)}
                  />
                )}

                {/* Attach / Change file */}
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="qu-hint-btn"
                    disabled={loading}
                    onClick={() =>
                      optionFileRefs.current[i] &&
                      optionFileRefs.current[i].click()
                    }
                    style={{ padding: "6px 10px", fontSize: 13 }}
                  >
                    {optionFiles[i] ? "Change Image" : "Attach Image"}
                  </button>

                  <input
                    ref={(el) => (optionFileRefs.current[i] = el)}
                    type="file"
                    accept=".jpeg,.jpg,.png"
                    className="qu-file-input"
                    style={{ display: "none" }}
                    disabled={loading}
                    onChange={(e) => handleOptionFileChange(i, e)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="character-limit">*Max length 150 characters. Option images: JPG/JPEG/PNG, max 1MB.</div>
        <div className="character-limit">*Recommmended size - 150x150 </div>

        <div className="qu-form-group">
          <label>Correct Answer</label>
          <div style={{ display: "flex", gap: "15px", marginTop: "6px" }}>
            {options.slice(0, optionsCount).map((_, i) => {
              const letter = String.fromCharCode(65 + i); // 65 = 'A'
              return (
                <label
                  key={i}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <input
                    type="radio"
                    name="correctAnswer"
                    value={String(i + 1)}
                    disabled={loading}
                    checked={correctAnswer === String(i + 1)}
                    onChange={(e) => setCorrectAnswer(e.target.value)}
                  />
                  {letter}
                </label>
              );
            })}
          </div>
        </div>

        <div className="qu-form-group">
          <label>Upload Hint (optional)</label>

          <div className="remove-button-container"></div>

          <div className="qu-upload-container">
            <button
              className="qu-hint-btn"
              disabled={loading}
              onClick={() => fileInputRef.current.click()}
            >
              Choose File
              <div className="qu-preview-container">
                {previewUrl ? (
                  // if we have a selected file, use its mime type
                  hintFile ? (
                    hintFile.type.startsWith("video/") ? (
                      <video
                        key={previewUrl}
                        className="qu-preview"
                        src={previewUrl}
                        controls
                      />
                    ) : hintFile.type.startsWith("audio/") ? (
                      <audio
                        key={previewUrl}
                        className="qu-preview qu-preview-audio"
                        src={previewUrl}
                        controls
                      />
                    ) : (
                      <img
                        key={previewUrl}
                        className="qu-preview"
                        src={previewUrl}
                        alt="Hint preview"
                      />
                    )
                  ) : // fallback for existingHint (S3 URL): check extension on URL
                  previewUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                    <video
                      key={previewUrl}
                      className="qu-preview"
                      src={previewUrl}
                      controls
                    />
                  ) : previewUrl.match(/\.(mp3|wav|m4a)$/i) ? (
                    <audio
                      key={previewUrl}
                      className="qu-preview qu-preview-audio"
                      src={previewUrl}
                      controls
                    />
                  ) : (
                    <img
                      key={previewUrl}
                      className="qu-preview"
                      src={previewUrl}
                      alt="Hint preview"
                    />
                  )
                ) : (
                  <span className="qu-file-name">
                    {existingHint || "No file selected"}
                  </span>
                )}
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpeg,.jpg,.png,.mp4,.mp3,.wav,.m4a,.webm,.ogg"
              className="qu-file-input"
              disabled={loading}
              onChange={handleFileChange}
            />

            {previewUrl ? (
              <button
                className="remove-image-button"
                onClick={handleRemoveImage}
              >
                Remove hint
              </button>
            ) : (
              ""
            )}
          </div>
          <div className="code">
            * Allowed media formats are JPG,JPEG,PNG,MP4,MP3 <br /> * Max file
            upload size limit 5MB
          </div>
        </div>

        <div className="qu-actions">
          <button
            className="qu-cancel-btn"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="qu-save-btn"
            disabled={loading}
            onClick={handleSubmit}
          >
            {questionId ? "Update" : "Save Question"}
          </button>
        </div>
      </div>
    </>
  );
}
